import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Accordion } from '../../../shared/Accordion';
import { Button } from '../../../shared/Button';

/**
 * WarningBox Component
 *
 * Displays a warning message with an icon
 */
function WarningBox({ message }) {
  return (
    <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
      <svg
        className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clipRule="evenodd"
        />
      </svg>
      <p className="text-sm text-red-800">{message}</p>
    </div>
  );
}

/**
 * DeleteAccordion Component
 *
 * Reusable delete/reject accordion for Image and Video modals.
 *
 * For images: Shows warning, reason textarea, and delete button
 * For videos: Shows warning and delete button (no reason required)
 *
 * @param {Object} props
 * @param {'image' | 'video'} props.variant - Which type of content
 * @param {string} props.reason - Delete reason (only for images)
 * @param {Function} props.onReasonChange - Reason change handler (only for images)
 * @param {Function} props.onDelete - Delete button click handler
 * @param {boolean} props.isDeleting - Whether deletion is in progress
 * @param {boolean} props.disabled - Whether the delete button is disabled
 */
export function DeleteAccordion({
  variant = 'image',
  reason = '',
  onReasonChange,
  onDelete,
  isDeleting = false,
  disabled = false,
}) {
  const { t } = useTranslation('approval');

  const isImage = variant === 'image';

  // Get localized strings based on variant
  const title = isImage
    ? t('modal.accordion.deleteTitle', { defaultValue: 'Delete Image' })
    : t('modal.video.deleteTitle', { defaultValue: 'Delete Video from Approval Queue' });

  const warning = isImage
    ? t('modal.delete.warning', {
        defaultValue: 'This action cannot be undone. The image will be permanently removed.',
      })
    : t('modal.video.deleteWarning', {
        defaultValue:
          'This action cannot be undone. The video will be permanently removed from the approval queue and will not be posted to Instagram.',
      });

  const buttonLabel = isImage
    ? t('modal.delete.button', { defaultValue: 'Delete Image' })
    : t('modal.video.deleteButton', { defaultValue: 'Delete Video Permanently' });

  const deletingLabel = isImage
    ? t('modal.delete.deleting', { defaultValue: 'Deleting...' })
    : t('modal.video.deleting', { defaultValue: 'Deleting...' });

  // For images, button is disabled if no reason provided
  const isButtonDisabled = isImage
    ? disabled || !reason.trim() || isDeleting
    : disabled || isDeleting;

  return (
    <Accordion
      title={title}
      icon="⚠️"
      defaultExpanded={false}
      variant="danger"
    >
      <div className="space-y-4 pt-4">
        {/* Warning Message */}
        <WarningBox message={warning} />

        {/* Reason Textarea (only for images) */}
        {isImage && (
          <div>
            <label
              htmlFor="deleteReason"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {t('modal.delete.reasonLabel', { defaultValue: 'Reason for deletion' })}
            </label>
            <textarea
              id="deleteReason"
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
              placeholder={t('modal.delete.reasonPlaceholder', {
                defaultValue: 'Please provide a reason for deleting this image...',
              })}
              value={reason}
              onChange={(e) => onReasonChange?.(e.target.value)}
              disabled={isDeleting}
            />
          </div>
        )}

        {/* Delete Button */}
        <Button
          variant="danger"
          onClick={onDelete}
          loading={isDeleting}
          disabled={isButtonDisabled}
          className="w-full"
        >
          {isDeleting ? deletingLabel : buttonLabel}
        </Button>
      </div>
    </Accordion>
  );
}

DeleteAccordion.propTypes = {
  variant: PropTypes.oneOf(['image', 'video']),
  reason: PropTypes.string,
  onReasonChange: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  isDeleting: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default DeleteAccordion;
